import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  FilePlus, MoreHorizontal, Eye, Trash2, Car, Building2, Handshake,
  ExternalLink, Search, User, ChevronDown, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { usePdfPreview } from "@/components/PdfPreviewDialog";

// -----------------------------------------------------------------------------
// Proposals — grouped by AGENCY (alphabetical) → AGENT within each agency.
// Filters at the top let admins/agency admins jump straight to one agency and/
// or one agent's proposals. Super admins see every proposal across the
// platform; agency admins see everyone in their agency; agents see their own
// scope (per `_proposal_scope_filter` server-side).
// -----------------------------------------------------------------------------
const UNASSIGNED = "__unassigned__";
const ALL = "__all__";

export default function Proposals() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "admin";
  const canFilterAgency = isSuperAdmin;

  const [items, setItems] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [agencyFilter, setAgencyFilter] = useState(ALL);
  const [agentFilter, setAgentFilter] = useState(ALL);
  const [collapsed, setCollapsed] = useState({}); // { agencyKey: bool }
  const { preview, dialog: pdfDialog } = usePdfPreview();

  const load = async () => {
    try {
      const [pRes, dRes] = await Promise.all([
        api.get("/proposals"),
        api.get("/dealers").catch(() => ({ data: [] })),
      ]);
      setItems(pRes.data || []);
      setDealers(dRes.data || []);
    } catch (e) {
      toast.error("Failed to load proposals");
    } finally { setLoading(false); }
  };
  // load() closes over api + toast which are module-scope and stable
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const findLinkedDealer = (dealership) => {
    if (!dealership) return null;
    const norm = dealership.trim().toLowerCase();
    return dealers.find((d) => (d.dealer_name || "").trim().toLowerCase() === norm) || null;
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this proposal?")) return;
    try {
      await api.delete(`/proposals/${id}`);
      setItems((prev) => prev.filter((p) => p.id !== id));
      toast.success("Proposal deleted");
    } catch { toast.error("Delete failed"); }
  };

  const download = (id, title) => preview({
    fetcher: () => api.get(`/proposals/${id}/pdf`, { responseType: "blob" }),
    filename: `${(title || "proposal").replace(/[^\w-]+/g, "_")}.pdf`,
    title: `Proposal · ${title || ""}`,
  });

  // Unique agencies & agents for the picker (respects text search so pickers
  // stay narrow when the user has typed something).
  const { agencies, agentsForAgency } = useMemo(() => {
    const ag = new Map(); // agencyKey → {label, count}
    const perAgencyAgents = new Map(); // agencyKey → Map(agentKey → {label, count})
    for (const p of items) {
      const agencyLabel = (p.agency || "").trim() || "Unassigned";
      const agencyKey = agencyLabel === "Unassigned" ? UNASSIGNED : agencyLabel;
      ag.set(agencyKey, {
        label: agencyLabel,
        count: (ag.get(agencyKey)?.count || 0) + 1,
      });
      const agentLabel = (p.agent_name || p.agent_email || "Unknown agent").trim();
      const agentKey = p.user_id || agentLabel;
      const bucket = perAgencyAgents.get(agencyKey) || new Map();
      bucket.set(agentKey, {
        label: agentLabel,
        email: p.agent_email,
        count: (bucket.get(agentKey)?.count || 0) + 1,
      });
      perAgencyAgents.set(agencyKey, bucket);
    }
    const agencies = Array.from(ag.entries())
      .map(([key, v]) => ({ key, ...v }))
      .sort((a, b) => a.label.localeCompare(b.label));
    const agentsForAgency = (agencyKey) => {
      const src = agencyKey === ALL
        ? Array.from(perAgencyAgents.values()).flatMap((m) => Array.from(m.entries()))
        : Array.from((perAgencyAgents.get(agencyKey) || new Map()).entries());
      const merged = new Map();
      for (const [key, v] of src) {
        merged.set(key, {
          label: v.label,
          email: v.email,
          count: (merged.get(key)?.count || 0) + v.count,
        });
      }
      return Array.from(merged.entries())
        .map(([key, v]) => ({ key, ...v }))
        .sort((a, b) => a.label.localeCompare(b.label));
    };
    return { agencies, agentsForAgency };
  }, [items]);

  // Reset agent filter whenever the agency filter changes (so a stale agent
  // key never hides all rows).
  useEffect(() => { setAgentFilter(ALL); }, [agencyFilter]);

  // Filter → then group by agency + agent for the UI.
  const grouped = useMemo(() => {
    const term = q.trim().toLowerCase();
    const filtered = items.filter((p) => {
      const agencyLabel = (p.agency || "").trim() || "Unassigned";
      const agencyKey = agencyLabel === "Unassigned" ? UNASSIGNED : agencyLabel;
      const agentKey = p.user_id || p.agent_name || p.agent_email || "";
      if (agencyFilter !== ALL && agencyKey !== agencyFilter) return false;
      if (agentFilter !== ALL && agentKey !== agentFilter) return false;
      if (!term) return true;
      return (
        (p.title || "").toLowerCase().includes(term) ||
        (p.dealership || "").toLowerCase().includes(term) ||
        (p.agent_name || "").toLowerCase().includes(term) ||
        (p.agent_email || "").toLowerCase().includes(term) ||
        (p.agency || "").toLowerCase().includes(term)
      );
    });

    // Group by agency
    const byAgency = new Map();
    for (const p of filtered) {
      const agencyLabel = (p.agency || "").trim() || "Unassigned";
      const agencyKey = agencyLabel === "Unassigned" ? UNASSIGNED : agencyLabel;
      const g = byAgency.get(agencyKey) || { key: agencyKey, label: agencyLabel, agents: new Map() };
      const agentLabel = (p.agent_name || p.agent_email || "Unknown agent").trim();
      const agentKey = p.user_id || agentLabel;
      const a = g.agents.get(agentKey) || { key: agentKey, label: agentLabel, email: p.agent_email, proposals: [] };
      a.proposals.push(p);
      g.agents.set(agentKey, a);
      byAgency.set(agencyKey, g);
    }
    return Array.from(byAgency.values())
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((g) => ({
        ...g,
        agents: Array.from(g.agents.values()).sort((a, b) => a.label.localeCompare(b.label)),
        proposalCount: Array.from(g.agents.values()).reduce((n, a) => n + a.proposals.length, 0),
      }));
  }, [items, q, agencyFilter, agentFilter]);

  const toggleAgency = (key) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  const hasResults = grouped.some((g) => g.proposalCount > 0);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8 sm:py-10" data-testid="proposals-page">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#D4AF37]">Marketing</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black text-[#0B132B] leading-none mt-1">Proposals</h1>
            <p className="text-sm text-[#6C757D] mt-2 max-w-xl">
              Every dealership pitch you've generated — grouped by agency, then by the salesperson who ran it.
              {isSuperAdmin && " Super-admin view: every proposal across the platform is visible here."}
            </p>
          </div>
          <Link to="/proposals/new" data-testid="proposals-new-cta">
            <Button className="h-11 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-semibold">
              <FilePlus size={14} className="mr-2" /> New proposal
            </Button>
          </Link>
        </div>

        {/* Filters row: search + agency picker + agent picker */}
        <Card className="bg-white border border-[#E9ECEF] rounded-sm p-4 mb-6" data-testid="proposals-filters">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6C757D]" />
              <Input
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search title, dealership, or agent…"
                className="pl-9 h-10 rounded-sm border-[#E9ECEF]"
                data-testid="proposals-search"
              />
            </div>
            {canFilterAgency ? (
              <Select value={agencyFilter} onValueChange={setAgencyFilter}>
                <SelectTrigger className="h-10 rounded-sm border-[#E9ECEF]" data-testid="proposals-agency-filter">
                  <div className="flex items-center gap-2 truncate">
                    <Building2 size={14} className="text-[#6C757D] shrink-0" />
                    <SelectValue placeholder="All agencies" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL} data-testid="proposals-agency-all">All agencies ({items.length})</SelectItem>
                  {agencies.map((a) => (
                    <SelectItem key={a.key} value={a.key} data-testid={`proposals-agency-${a.key}`}>
                      {a.label} ({a.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="h-10 rounded-sm border border-[#E9ECEF] px-3 flex items-center gap-2 text-xs text-[#6C757D] bg-[#F8F9FA]">
                <Building2 size={14} className="shrink-0" />
                <span className="truncate">{user?.agency || "Your agency"}</span>
              </div>
            )}
            <Select value={agentFilter} onValueChange={setAgentFilter}>
              <SelectTrigger className="h-10 rounded-sm border-[#E9ECEF]" data-testid="proposals-agent-filter">
                <div className="flex items-center gap-2 truncate">
                  <User size={14} className="text-[#6C757D] shrink-0" />
                  <SelectValue placeholder="All agents" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL} data-testid="proposals-agent-all">All agents</SelectItem>
                {agentsForAgency(agencyFilter).map((a) => (
                  <SelectItem key={a.key} value={a.key} data-testid={`proposals-agent-${a.key}`}>
                    {a.label} ({a.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>

        {loading ? (
          <div className="text-sm text-[#6C757D] py-10">Loading proposals…</div>
        ) : !hasResults ? (
          <Card className="border-dashed border-2 border-[#E9ECEF] bg-white p-14 text-center rounded-sm" data-testid="proposals-empty">
            <FilePlus size={28} className="mx-auto text-[#6C757D]" />
            <h3 className="font-display text-xl font-bold text-[#0B132B] mt-3">
              {(q || agencyFilter !== ALL || agentFilter !== ALL) ? "No matches" : "No proposals yet"}
            </h3>
            {!(q || agencyFilter !== ALL || agentFilter !== ALL) && (
              <Link to="/proposals/new">
                <Button className="mt-4 h-10 rounded-sm bg-[#0B132B] text-white">New proposal</Button>
              </Link>
            )}
          </Card>
        ) : (
          <div className="space-y-8" data-testid="proposals-groups">
            {grouped.map((g) => (
              <section key={g.key} data-testid={`proposals-agency-${g.key}`}>
                <button
                  type="button"
                  onClick={() => toggleAgency(g.key)}
                  className="w-full flex items-center gap-3 mb-3 group"
                  data-testid={`proposals-agency-toggle-${g.key}`}
                >
                  {collapsed[g.key]
                    ? <ChevronRight size={16} className="text-[#6C757D]" />
                    : <ChevronDown size={16} className="text-[#6C757D]" />}
                  <Building2 size={16} className="text-[#0B132B]" />
                  <h2 className="font-display text-lg font-bold text-[#0B132B] group-hover:text-[#D4AF37] transition-colors">{g.label}</h2>
                  <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#6C757D]">
                    {g.proposalCount} {g.proposalCount === 1 ? "proposal" : "proposals"}
                  </span>
                  <div className="flex-1 h-px bg-[#E9ECEF] ml-2" />
                </button>

                {!collapsed[g.key] && (
                  <div className="space-y-6">
                    {g.agents.map((a) => (
                      <div key={a.key} data-testid={`proposals-agent-${g.key}-${a.key}`}>
                        <div className="flex items-center gap-2 mb-3 pl-6">
                          <User size={12} className="text-[#6C757D]" />
                          <div className="text-xs font-bold text-[#0B132B]">{a.label}</div>
                          {a.email && <div className="text-[11px] text-[#6C757D]">· {a.email}</div>}
                          <div className="text-[10px] uppercase tracking-[0.16em] text-[#6C757D] ml-1">· {a.proposals.length}</div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pl-6">
                          {a.proposals.map((p) => (
                            <ProposalTile
                              key={p.id}
                              p={p}
                              linkedDealer={findLinkedDealer(p.dealership)}
                              onDelete={() => remove(p.id)}
                              onDownload={() => download(p.id, p.title)}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            ))}
          </div>
        )}
      </main>
      {pdfDialog}
    </div>
  );
}

function ProposalTile({ p, linkedDealer, onDelete, onDownload }) {
  const firstImg = p.vehicles?.[0]?.vehicle?.image_url;
  const st = linkedDealer?.status;
  return (
    <Card className="group bg-white border border-[#E9ECEF] rounded-sm overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all" data-testid={`proposal-tile-${p.id}`}>
      <div className="relative h-32 bg-[#0B132B] overflow-hidden">
        {firstImg ? (
          <img src={firstImg} alt="" className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#0B132B] to-[#1C2541] flex items-center justify-center">
            <Car className="text-[#D4AF37]" size={36} />
          </div>
        )}
        <div className="absolute top-2 left-2 px-2 py-1 rounded-sm bg-[#D4AF37] text-[#0B132B] text-[10px] uppercase tracking-[0.2em] font-bold">
          {p.vehicles?.length || 0} vehicles
        </div>
        <div className="absolute top-2 right-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="secondary" className="h-8 w-8 rounded-sm bg-white/95 hover:bg-white" data-testid={`proposal-menu-${p.id}`}>
                <MoreHorizontal size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem asChild data-testid={`proposal-edit-${p.id}`}>
                <Link to={`/proposals/${p.id}`}>
                  <Eye size={14} className="mr-2" /> Open / edit
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onDownload} data-testid={`proposal-download-${p.id}`}>
                <Eye size={14} className="mr-2" /> Preview & Download
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={`/proposals/${p.id}/view`} target="_blank" rel="noopener noreferrer">
                  <ExternalLink size={14} className="mr-2" /> Open view
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onDelete} className="text-red-600" data-testid={`proposal-delete-${p.id}`}>
                <Trash2 size={14} className="mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="p-4">
        <Link to={`/proposals/${p.id}`} className="block group-hover:text-[#D4AF37] transition-colors">
          <h3 className="font-display text-base font-bold text-[#0B132B] leading-snug line-clamp-2" data-testid={`proposal-title-${p.id}`}>{p.title}</h3>
        </Link>
        {p.dealership && (
          <div className="mt-2 text-xs text-[#6C757D] flex items-center gap-1.5"><Building2 size={12} /> {p.dealership}</div>
        )}
        {linkedDealer ? (
          <div className={`mt-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] font-bold px-2 py-1 rounded-sm border ${
            st === "executed" ? "bg-green-50 border-green-200 text-green-700" :
            st === "dealer_signed" ? "bg-amber-50 border-amber-200 text-amber-800" :
            st === "sent" ? "bg-blue-50 border-blue-200 text-blue-700" :
            "bg-[#F8F9FA] border-[#E9ECEF] text-[#6C757D]"
          }`} data-testid={`proposal-dealer-link-${p.id}`}>
            <Handshake size={11}/> {st === "executed" ? "Signed & executed" : st === "dealer_signed" ? "Dealer signed" : st === "sent" ? "Sent to dealer" : "Draft"}
          </div>
        ) : p.dealership ? (
          <div className="mt-3 text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D]">No linked signup</div>
        ) : null}
      </div>
    </Card>
  );
}
